import { useEffect, useMemo, useState } from 'react';
import { AvailableDate, AvailableTime } from '../../../../types/AvailabilityModels';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { slotClashesWithExistingBookings } from '../../../../utils/bookingClash';
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconCalendar,
  IconCheck,
  IconChevronRight,
} from '@tabler/icons-react';
import { primaryColor } from '../../../../constants/theme';
import { useApp } from '../../../../context/AppContext';
import { TopicSlotModel } from '../../../../types/TopicModels';

interface TimeSelectorProps {
  availableTimes: AvailableTime[];
  allTopics?: TopicSlotModel[];
  existingBookings?: BookingResponseModel[];
  selectedTopicSelections?: TopicSelection[];
  onApplyTopicSelection?: (selection: TopicSelection) => void;
}

export interface TopicSelection {
  topicKey: string;
  topic: TopicSlotModel | null;
  times: AvailableTime[];
}

interface TopicOption {
  topicKey: string;
  topic: TopicSlotModel | null;
  times: AvailableTime[];
}

const getTimeRangeText = (time: AvailableTime) => {
  return `${new Date(time.startIso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })} - ${new Date(time.endIso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const getSlotPrice = (time: AvailableTime, topic: TopicSlotModel | null) => {
  return time.rate ?? topic?.rate ?? 0;
};

const getTopicKey = (topic: TopicSlotModel | null) => {
  return topic?.id ? `topic-${topic.id}` : 'open-session';
};

const getSlotKey = (time: AvailableTime) => {
  return time.id ? `time-${time.id}` : `${time.startIso}-${time.endIso}`;
};

const getTopicDuration = (
  topic: TopicSlotModel | null,
  topicTimes: AvailableTime[]
) => {
  if (topic?.durationMinutes) {
    return topic.durationMinutes;
  }

  if (!topicTimes.length) {
    return 0;
  }

  const firstSlot = topicTimes[0];
  const minutes =
    (new Date(firstSlot.endIso).getTime() - new Date(firstSlot.startIso).getTime()) /
    (1000 * 60);

  return Math.max(Math.round(minutes), 0);
};

/**
 * Returns true if two time ranges overlap (exclusive endpoints — touching is not
 * considered an overlap so consecutive slots don't block each other).
 */
const timesOverlap = (a: AvailableTime, b: AvailableTime): boolean => {
  const aStart = new Date(a.startIso).getTime();
  const aEnd = new Date(a.endIso).getTime();
  const bStart = new Date(b.startIso).getTime();
  const bEnd = new Date(b.endIso).getTime();
  return aStart < bEnd && bStart < aEnd;
};

export const TimeSelector = ({
  availableTimes,
  allTopics = [],
  existingBookings = [],
  selectedTopicSelections = [],
  onApplyTopicSelection,
}: TimeSelectorProps) => {
  const { scrollAreaHeight } = useApp();
  const isMobile = useMediaQuery('(max-width: 640px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const [selectedTopicOption, setSelectedTopicOption] = useState<TopicOption | null>(
    null
  );

  const descriptionCharLimit = useMemo(() => {
    if (isMobile) return 30;
    if (isTablet) return 60;
    return 100;
  }, [isMobile, isTablet]);

  const topicOptions = useMemo(() => {
    const topicMap = new Map<string, TopicOption>();

    // Add all topics from allTopics
    allTopics.forEach((topic) => {
      const topicKey = getTopicKey(topic);
      if (!topicMap.has(topicKey)) {
        topicMap.set(topicKey, { topicKey, topic, times: [] });
      }
    });

    // Populate times from availableTimes and add open session if needed
    availableTimes.forEach((time) => {
      const topics = time.topics ?? [];

      if (topics.length === 0) {
        const topicKey = getTopicKey(null);
        if (!topicMap.has(topicKey)) {
          topicMap.set(topicKey, { topicKey, topic: null, times: [] });
        }

        const openTopic = topicMap.get(topicKey);
        if (openTopic) {
          const slotKey = getSlotKey(time);
          if (!openTopic.times.some((slot) => getSlotKey(slot) === slotKey)) {
            openTopic.times.push(time);
          }
        }

        return;
      }

      topics.forEach((topic) => {
        const topicKey = getTopicKey(topic);
        if (!topicMap.has(topicKey)) {
          topicMap.set(topicKey, { topicKey, topic, times: [] });
        }

        const topicOption = topicMap.get(topicKey);
        if (topicOption) {
          const slotKey = getSlotKey(time);
          if (!topicOption.times.some((slot) => getSlotKey(slot) === slotKey)) {
            topicOption.times.push(time);
          }
        }
      });
    });

    return Array.from(topicMap.values()).map((option) => ({
      ...option,
      times: [...option.times].sort(
        (a, b) => new Date(a.startIso).getTime() - new Date(b.startIso).getTime()
      ),
    }));
  }, [availableTimes, allTopics]);

  const selectedByTopic = useMemo(() => {
    return new Map(
      selectedTopicSelections.map((selection) => [selection.topicKey, selection])
    );
  }, [selectedTopicSelections]);

  /**
   * Map from slotKey → owning topicKey for overlap detection.
   * We build a flat list of all selected {time, topicKey} pairs so the modal can
   * check both exact-key and time-range overlap when rendering its buttons.
   */
  const selectedTimesAcrossTopics = useMemo(() => {
    return selectedTopicSelections.flatMap((selection) =>
      selection.times.map((time) => ({
        time,
        topicKey: selection.topicKey,
      }))
    );
  }, [selectedTopicSelections]);

  const totalSelectedCount = useMemo(() => {
    return selectedTopicSelections.reduce(
      (sum, selection) => sum + selection.times.length,
      0
    );
  }, [selectedTopicSelections]);

  // Derive a numeric pixel height for the available-topics scroll area.
  // We subtract 310 (calendar + booking form minimum) instead of 420 so more
  // topic cards are visible.
  const topicsAreaHeight = useMemo(() => {
    const raw = Number(scrollAreaHeight.replace('px', ''));
    if (Number.isNaN(raw)) {
      return 400;
    }

    return Math.max(raw - 600, 300);
  }, [scrollAreaHeight]);

  return (
    <Stack>
      <Group gap={5}>
        <Title order={4} fw="normal">
          Choose a topic to book
        </Title>
        {totalSelectedCount > 0 && (
          <Badge radius="sm" variant="light" color={primaryColor} data-testid="selected-count-badge">
            {totalSelectedCount} selected
          </Badge>
        )}
      </Group>
      <ScrollArea
        // mah={topicsAreaHeight}
        // type="auto"
        offsetScrollbars
      >
        {!topicOptions.length && (
          <Text c="dimmed" ta="center" py={20}>
            No available topics
          </Text>
        )}

        <Stack gap="sm">
          {topicOptions.map((option) => {
            const selectedForTopic = selectedByTopic.get(option.topicKey);
            const selectedCount = selectedForTopic?.times.length ?? 0;
            const selectedTotal =
              selectedForTopic?.times.reduce(
                (total, time) => total + getSlotPrice(time, option.topic),
                0
              ) ?? 0;
            const color = option.topic?.colour ?? primaryColor;
            const duration = getTopicDuration(option.topic, option.times);
            const hasAvailability = option.times.length > 0;

            return (
              <Card
                key={option.topicKey}
                withBorder
                radius="md"
                p="sm"
                style={{
                  borderColor: hasAvailability ? '#b0b0b0' : '#d0d0d0',
                  borderWidth: 1,
                  cursor: hasAvailability ? 'pointer' : 'not-allowed',
                  opacity: hasAvailability ? 1 : 0.5,
                }}
                onClick={() => hasAvailability && setSelectedTopicOption(option)}
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group wrap="nowrap" gap="sm" style={{ flex: 1, alignItems: 'flex-start' }}>
                    <div
                      style={{
                        width: 4,
                        minHeight: 80,
                        borderRadius: 8,
                        backgroundColor: color,
                        marginTop: 2,
                      }}
                    />
                    <Stack gap={6} style={{ flex: 1, alignItems: 'flex-start' }}>
                      <Text fw={700} ta="left" w="100%">
                        {option.topic?.title || 'Open Session'}
                      </Text>
                      <Text size="sm" c="dimmed" ta="left" w="100%" truncate>
                        {(option.topic?.description || 'No description provided').length > descriptionCharLimit
                          ? `${(option.topic?.description || 'No description provided').slice(0, descriptionCharLimit)}...`
                          : option.topic?.description || 'No description provided'}
                      </Text>
                      <Group gap={6}>
                        {duration > 0 && (
                          <Badge radius="sm" color="gray" variant="outline" c="black">
                            {duration} mins
                          </Badge>
                        )}
                        {option.times.length > 0 ? (
                          <Badge radius="sm" color="gray" variant="outline" c="black">
                            £{getSlotPrice(option.times[0], option.topic)}
                          </Badge>
                        ) : (
                          <Badge radius="sm" color="gray" variant="outline" c="dimmed">
                            No availability
                          </Badge>
                        )}
                        {selectedCount > 0 && (
                          <Badge radius="sm" color={primaryColor} variant="outline">
                            {selectedCount} selected · £{selectedTotal}
                          </Badge>
                        )}
                      </Group>
                    </Stack>
                  </Group>

                  <IconChevronRight size={20} color="#666" />
                </Group>
              </Card>
            );
          })}
        </Stack>
      </ScrollArea>

      <TopicSlotsModal
        option={selectedTopicOption}
        selectedTimes={
          selectedTopicOption
            ? selectedByTopic.get(selectedTopicOption.topicKey)?.times ?? []
            : []
        }
        selectedTimesAcrossTopics={selectedTimesAcrossTopics}
        existingBookings={existingBookings}
        onClose={() => setSelectedTopicOption(null)}
        onApply={(times) => {
          if (!selectedTopicOption) {
            return;
          }

          onApplyTopicSelection?.({
            topicKey: selectedTopicOption.topicKey,
            topic: selectedTopicOption.topic,
            times,
          });
          setSelectedTopicOption(null);
        }}
      />
    </Stack>
  );
};

const TopicSlotsModal = ({
  option,
  selectedTimes,
  selectedTimesAcrossTopics,
  existingBookings,
  onClose,
  onApply,
}: {
  option: TopicOption | null;
  selectedTimes: AvailableTime[];
  selectedTimesAcrossTopics: { time: AvailableTime; topicKey: string }[];
  existingBookings: BookingResponseModel[];
  onClose: () => void;
  onApply: (times: AvailableTime[]) => void;
}) => {
  const opened = !!option;
  const [workingSelection, setWorkingSelection] = useState<AvailableTime[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [datePickerModalOpened, setDatePickerModalOpened] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);

  const availableDates = useMemo((): AvailableDate[] => {
    if (!option) return [];
    const dateMap = new Map<string, AvailableDate>();
    option.times.forEach((time) => {
      const d = new Date(time.startIso);
      const dateValue = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
      if (!dateMap.has(dateValue)) {
        dateMap.set(dateValue, { date: dateValue, availableTimes: [time], available: true, currency: '', timeZoneId: '' });
      } else {
        dateMap.get(dateValue)!.availableTimes.push(time);
      }
    });
    return Array.from(dateMap.values());
  }, [option]);

  useEffect(() => {
    setWorkingSelection(selectedTimes);
  }, [selectedTimes, option?.topicKey]);

  useEffect(() => {
    if (availableDates.length > 0) {
      setSelectedDate(new Date(availableDates[0].availableTimes[0].startIso));
    } else {
      setSelectedDate(null);
    }
  }, [option?.topicKey]);

  const filteredTimes = useMemo(() => {
    if (!option || !selectedDate) return option?.times ?? [];
    return option.times.filter(
      (time) => new Date(time.startIso).toDateString() === selectedDate.toDateString()
    );
  }, [option, selectedDate]);

  const selectedSlotKeys = useMemo(() => {
    return new Set(workingSelection.map((time) => getSlotKey(time)));
  }, [workingSelection]);

  const totalPrice = workingSelection.reduce(
    (total, time) => total + getSlotPrice(time, option?.topic ?? null),
    0
  );

  const color = option?.topic?.colour ?? primaryColor;
  const duration = getTopicDuration(option?.topic ?? null, option?.times ?? []);

  return (
    <>
      <Modal opened={opened} onClose={onClose} title="Select slots" centered size="xl">
        {option && (
          <Stack gap="sm">
            {/* Topic header card */}
            <Card
              withBorder
              radius="md"
              p="md"
              style={{
                borderColor: color,
                background: `${color}1a`,
              }}
            >
              <Stack gap={6} align="flex-start">
                <Group gap={6} align="center" wrap="nowrap">
                  <Text fw={700} size="md" ta="left">
                    {option.topic?.title || 'Open Session'}
                  </Text>
                  <IconCheck size={16} color={color} />
                </Group>
                <Text c="dimmed" size="sm" ta="left">
                  {duration} mins per session
                </Text>
                {option.topic?.description && (
                  <Text c="dimmed" size="sm" ta="left">
                    {option.topic.description}
                  </Text>
                )}
              </Stack>
            </Card>

            {/* Date picker */}
            <Group justify="space-between" align="center">
              <Title order={4} fw="normal">
                Date
              </Title>
              <Group 
                gap={8} 
                align="center"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setDatePickerModalOpened(true);
                  setSelectedMonth(selectedDate || new Date());
                }}
              >
                <IconCalendar size={20} color={primaryColor} />
                <Text fw={500} c={primaryColor}>Month</Text>
              </Group>
            </Group>
            <ScrollArea type="auto" w="100%" offsetScrollbars>
              <Group wrap="nowrap" gap={8} py={4} style={{ minWidth: 'max-content' }}>
                {availableDates.map((avDate) => {
                  const dateObj = new Date(avDate.availableTimes[0].startIso);
                  const isSelected =
                    selectedDate?.toDateString() === dateObj.toDateString();
                  const slotCount = avDate.availableTimes.length;
                  return (
                    <Card
                      key={avDate.date}
                      withBorder
                      radius="lg"
                      p="xs"
                      style={{
                        height: 100,
                        width: 75,
                        cursor: 'pointer',
                        borderColor: isSelected ? primaryColor : '#3f4658',
                        borderWidth: isSelected ? 2 : 1,
                        background: isSelected ? `${primaryColor}1a` : undefined,
                        textAlign: 'center',
                      }}
                      onClick={() => setSelectedDate(dateObj)}
                    >
                      <Stack gap={2} align="center">
                        <Text size="xs" c={isSelected ? primaryColor : 'dimmed'} fw={600}>
                          {dateObj.toLocaleDateString('en-GB', { weekday: 'short' })}
                        </Text>
                        <Text size="lg" fw={700} c={isSelected ? primaryColor : undefined} lh={1}>
                          {dateObj.getDate()}
                        </Text>
                        <Text size="xs" c={isSelected ? primaryColor : 'dimmed'}>
                          {dateObj.toLocaleDateString('en-GB', { month: 'short' })}
                        </Text>
                        <Badge radius="xl" size="xs" color={isSelected ? primaryColor : 'gray'} variant={isSelected ? 'filled' : 'outline'} mt={2} styles={{ label: { fontSize: 7 } }}>
                          {slotCount} slots
                        </Badge>
                      </Stack>
                    </Card>
                  );
                })}
              </Group>
            </ScrollArea>

            {/* Section header */}
            <Group justify="space-between" align="center">
              <Title order={4} fw="normal">
                Available Times ({workingSelection.length})
              </Title>
              <Text fw={700} c={primaryColor}>
                Total: £{totalPrice}
              </Text>
            </Group>

            {/* Time slot grid */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              {filteredTimes.map((time) => {
                const slotKey = getSlotKey(time);

                // A slot is blocked if it overlaps with any slot already selected
                // for a *different* topic (exact-key match or time-range overlap).
                const blockedByOtherTopic = selectedTimesAcrossTopics.some(
                  ({ time: otherTime, topicKey: otherTopicKey }) =>
                    otherTopicKey !== option.topicKey &&
                    (getSlotKey(otherTime) === slotKey ||
                      timesOverlap(time, otherTime))
                );

                const clashesWithExisting = slotClashesWithExistingBookings(
                  time,
                  existingBookings
                );

                const isDisabled = clashesWithExisting || blockedByOtherTopic;

                const selected = selectedSlotKeys.has(slotKey);

                const disabledMessage = clashesWithExisting
                  ? 'Clashes with existing Booking'
                  : 'Selected for another topic';

                const buttonContent = isDisabled ? (
                  <Text size="sm" ta="center" w="100%" truncate="end">
                    {disabledMessage}
                  </Text>
                ) : (
                  <Box
                    style={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) 56px',
                      alignItems: 'center',
                      columnGap: 8,
                    }}
                  >
                    <Text size="sm" ta="left" truncate="end">
                      {getTimeRangeText(time)}
                    </Text>
                    <Text
                      size="sm"
                      c={selected ? 'white' : 'dimmed'}
                      ta="right"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      £{getSlotPrice(time, option.topic)}
                    </Text>
                  </Box>
                );

                const button = (
                  <Button
                    key={slotKey}
                    variant={selected ? 'filled' : 'outline'}
                    fullWidth
                    color={
                      selected
                        ? primaryColor
                        : clashesWithExisting
                          ? 'red'
                          : blockedByOtherTopic
                            ? 'gray'
                            : 'dark'
                    }
                    justify="space-between"
                    styles={{
                      inner: {
                        width: '100%',
                      },
                      label: {
                        width: '100%',
                      },
                    }}
                    disabled={isDisabled}
                    onClick={() => {
                      setWorkingSelection((previous) => {
                        const exists = previous.some(
                          (t) => getSlotKey(t) === slotKey
                        );

                        if (exists) {
                          return previous.filter((t) => getSlotKey(t) !== slotKey);
                        }

                        return [...previous, time].sort(
                          (a, b) =>
                            new Date(a.startIso).getTime() -
                            new Date(b.startIso).getTime()
                        );
                      });
                    }}
                  >
                    {buttonContent}
                  </Button>
                );

                if (isDisabled) {
                  return (
                    <Tooltip key={slotKey} label={disabledMessage} withArrow>
                      <Box>{button}</Box>
                    </Tooltip>
                  );
                }

                return button;
              })}
            </SimpleGrid>

            <Button
              color={primaryColor}
              fullWidth
              mt="xs"
              onClick={() => onApply(workingSelection)}
            >
              Apply to Booking
            </Button>
          </Stack>
        )}
      </Modal>

      {/* Date Picker Modal */}
      <DatePickerModal
        opened={datePickerModalOpened}
        onClose={() => setDatePickerModalOpened(false)}
        availableDates={availableDates}
        selectedDate={selectedDate}
        selectedMonth={selectedMonth}
        onSelectMonth={setSelectedMonth}
        onSelectDate={(date) => {
          setSelectedDate(date);
          setDatePickerModalOpened(false);
        }}
      />
    </>
  );
};

interface DatePickerModalProps {
  opened: boolean;
  onClose: () => void;
  availableDates: AvailableDate[];
  selectedDate: Date | null;
  selectedMonth: Date | null;
  onSelectMonth: (date: Date) => void;
  onSelectDate: (date: Date) => void;
}

const DatePickerModal = ({
  opened,
  onClose,
  availableDates,
  selectedDate,
  selectedMonth,
  onSelectMonth,
  onSelectDate,
}: DatePickerModalProps) => {
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(selectedDate);

  useEffect(() => {
    if (opened) {
      setTempSelectedDate(selectedDate);
    }
  }, [opened, selectedDate]);

  const availableDateSet = useMemo(() => {
    return new Set(
      availableDates.map((ad) => new Date(ad.availableTimes[0].startIso).toDateString())
    );
  }, [availableDates]);

  const allFutureMonths = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const months: Date[] = [];
    let current = new Date(today);

    // Generate months for the next 12 months
    for (let i = 0; i < 12; i++) {
      months.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    return months;
  }, []);

  const calendarDates = useMemo(() => {
    if (!selectedMonth) return [];

    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();

    // Get first day of month and number of days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const dates: (Date | null)[] = [];

    // Add empty slots for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      dates.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(new Date(year, month, day));
    }

    return dates;
  }, [selectedMonth]);

  return (
    <Modal opened={opened} onClose={onClose} title="Select Date" centered size="md">
      <Stack gap="lg">
        {/* Month Selection */}
        <Box>
          <ScrollArea type="auto" offsetScrollbars w="100%">
            <Group wrap="nowrap" gap={4} py={4} style={{ minWidth: 'max-content' }}>
              {allFutureMonths.map((month) => {
                const isSelected =
                  selectedMonth?.getFullYear() === month.getFullYear() &&
                  selectedMonth?.getMonth() === month.getMonth();

                return (
                  <Stack
                    key={`${month.getFullYear()}-${month.getMonth()}`}
                    align="center"
                    gap={4}
                    style={{ minWidth: 65, alignItems: 'center' }}
                  >
                    <Button
                      variant={isSelected ? 'filled' : 'outline'}
                      color={isSelected ? primaryColor : 'gray'}
                      onClick={() => onSelectMonth(month)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                        padding: 0,
                        borderRadius: '50%',
                      }}
                    >
                      <Text size="xs" fw={600}>
                        {month.toLocaleDateString('en-GB', { month: 'short' })}
                      </Text>
                    </Button>
                    <Text size="10px" c={isSelected ? primaryColor : 'dimmed'} fw={500}>
                      {month.getFullYear()}
                    </Text>
                  </Stack>
                );
              })}
            </Group>
          </ScrollArea>
        </Box>

        {/* Calendar Grid */}
        {selectedMonth && (
          <Box>
            {/* Day headers */}
            <SimpleGrid cols={7} spacing={4} mb="xs">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Box key={day} style={{ textAlign: 'center' }}>
                  <Text size="xs" fw={600} c="dimmed">
                    {day}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>

            {/* Date buttons */}
            <SimpleGrid cols={7} spacing={4}>
              {calendarDates.map((date, index) => {
                if (!date) {
                  return <Box key={`empty-${index}`} />;
                }

                const isAvailable = availableDateSet.has(date.toDateString());
                const isSelected =
                  tempSelectedDate?.toDateString() === date.toDateString();

                return (
                  <Button
                    key={date.toDateString()}
                    variant={isSelected ? 'filled' : isAvailable ? 'outline' : 'subtle'}
                    color={isSelected ? primaryColor : 'gray'}
                    disabled={!isAvailable}
                    onClick={() => setTempSelectedDate(date)}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      padding: 0,
                      fontSize: 12,
                    }}
                  >
                    {date.getDate()}
                  </Button>
                );
              })}
            </SimpleGrid>
          </Box>
        )}

        {/* Set Button */}
        <Button
          color={primaryColor}
          fullWidth
          onClick={() => {
            if (tempSelectedDate) {
              onSelectDate(tempSelectedDate);
            }
          }}
        >
          Set Date
        </Button>
      </Stack>
    </Modal>
  );
};

export const TimeButton = ({
  selectedTimes,
  time,
  onSelect,
}: {
  selectedTimes: AvailableTime[];
  time: AvailableTime;
  onSelect: (time: AvailableTime) => void;
}) => {
  const isTimeSelected = useMemo(() => {
    return selectedTimes.some(
      (selected) =>
        selected.startIso === time.startIso && selected.endIso === time.endIso
    );
  }, [selectedTimes, time]);

  return (
    <Button
      variant={isTimeSelected ? 'filled' : 'outline'}
      color={isTimeSelected ? primaryColor : 'black'}
      key={time.startIso + time.endIso}
      onClick={() => onSelect(time)}
      p={0}
    >
      <Group gap={5} align="center" justify="center">
        <Text>
          {new Date(time.startIso).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
          })}
          &nbsp;:&nbsp;
          {new Date(time.endIso).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
        <Text c={isTimeSelected ? 'white' : 'dimmed'}>
          (£{time?.rate ?? 'Free'})
        </Text>
      </Group>
    </Button>
  );
};
